<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h3 class="mx-3">Food</h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form ref="foodForm" v-model="valid" lazy-validation>
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                class="ma-3"
                v-model="date"
                label="Date"
                readonly
                required
                :rules="[() => !!date || 'Date is required']"
                v-on="on"
                ><v-icon small slot="prepend">
                  mdi-calendar
                </v-icon>
              </v-text-field>
            </template>
            <v-date-picker v-model="date" no-title @input="dateMenu = false">
            </v-date-picker>
          </v-menu>
          <v-menu
            ref="timeModal"
            v-model="timeMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="time"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                class="ma-3"
                v-model="time"
                label="Time"
                readonly
                required
                :rules="[() => !!time || 'Time is required']"
                v-on="on"
                ><v-icon small slot="prepend">
                  mdi-clock-outline
                </v-icon>
              </v-text-field>
            </template>
            <v-time-picker
              v-model="time"
              format="24hr"
              :allowed-minutes="(m) => m % 5 === 0"
              @click:minute="$refs.timeModal.save(time)"
            ></v-time-picker>
          </v-menu>
          <v-textarea
            class="ma-3"
            label="Description"
            required
            :rules="[() => !!description || 'Description is required']"
            rows="2"
            v-model.lazy="description"
            ><v-icon small slot="prepend">
              mdi-text
            </v-icon>
          </v-textarea>
          <v-combobox
            class="ma-3"
            label="Food Type"
            required
            :rules="[() => !!foodType || 'Food Type is required']"
            small-chips
            deletable-chips
            :items="itemsFoodType"
            v-model="foodType"
          >
            <v-icon small slot="prepend">
              mdi-food-fork-drink
            </v-icon>
          </v-combobox>
          <v-combobox
            class="ma-3"
            label="Trip"
            small-chips
            deletable-chips
            :items="itemsTrip"
            v-model="trip"
          >
            <v-icon small slot="prepend">
              mdi-map-marker-outline
            </v-icon>
          </v-combobox>
          <v-row class="justify-center">
            <v-slider
              class="ma-5 caption"
              v-model="health"
              :color="healthColor"
              :tick-labels="healthList"
              :max="4"
              step="1"
              ticks="always"
              tick-size="4"
              ><v-icon small slot="prepend">
                mdi-heart-outline
              </v-icon>
            </v-slider>
          </v-row>
        </v-form>
        <v-row class="justify-center">
          <v-btn
            class="mx-3"
            small
            fab
            color="secondary"
            :disabled="!valid"
            @click="insertFoodRecord"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-snackbar
          v-model="snackbarModel"
          bottom
          :color="snackbarColor"
          :timeout="snackbarTimeout"
        >
          {{ snackbarText }}
          <v-btn dark text @click="snackbarModel = false">
            Close
          </v-btn>
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import { mapState, mapActions } from "vuex";
import getsheetobj from "@/mixins/getsheetobj";

export default {
  name: "Food",
  mixins: [getsheetobj],
  data: () => ({}),
  watch: {
    trip() {
      // Check if trip is new, and not empty and not already in the tripList
      if (
        typeof this.trip === "string" &&
        this.trip !== "" &&
        !this.itemsTrip.includes(this.trip)
      ) {
        // create a new row for the trip
        const id = this.sheetsObj["trip"].maxId + 1;
        const uuid = uuidv4();
        const newTrip = this.trip;
        const params = {
          spreadsheetId: this.sheetId,
          valueInputOption: "RAW",
          range: "trip",
          insertDataOption: "INSERT_ROWS",
        };
        const valueRangeBody = {
          majorDimension: "ROWS",
          values: [[id, uuid, newTrip]],
        };
        if (this.isAuthenticated) {
          // update the sheet
          this.$gapi.getGapiClient().then((gapi) => {
            gapi.client.sheets.spreadsheets.values
              .append(params, valueRangeBody)
              .then(() => {
                // fecth the updated sheets
                this.getEditFormFields(this.sheetId, this.allSheets);
              });
          });
        }
      }
      // if not new, simply return the trip
      return this.trip;
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: (state) => state.auth.isAuthenticated,
      sheetId: (state) => state.sheet.sheetId,
      foodSheet: (state) => state.food.foodSheet,
      sheetsObj: (state) => state.sheet.sheetsObj,
      allSheets: (state) => state.sheet.allSheets,
      itemsFoodType: (state) => state.edit.lists.meals,
      itemsTrip: (state) => state.edit.lists.trip,
      healthList: (state) => state.food.health.list,
      snackbarText: (state) => state.food.snackbar.text,
      snackbarColor: (state) => state.food.snackbar.color,
      snackbarTimeout: (state) => state.food.snackbar.timeout,
    }),
    valid: {
      get() {
        return this.$store.state.food.valid;
      },
      set(value) {
        const payload = {
          state: "valid",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    date: {
      get() {
        return this.$store.state.food.date.date;
      },
      set(value) {
        const payload = {
          state: "date",
          field: "date",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    dateMenu: {
      get() {
        return this.$store.state.food.date.menu;
      },
      set(value) {
        const payload = {
          state: "date",
          field: "menu",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    time: {
      get() {
        return this.$store.state.food.time.time;
      },
      set(value) {
        const payload = {
          state: "time",
          field: "time",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    timeMenu: {
      get() {
        return this.$store.state.food.time.menu;
      },
      set(value) {
        const payload = {
          state: "time",
          field: "menu",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    description: {
      get() {
        return this.$store.state.food.description;
      },
      set(value) {
        const payload = {
          state: "description",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    foodType: {
      get() {
        return this.$store.state.food.foodType;
      },
      set(value) {
        const payload = {
          state: "foodType",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    trip: {
      get() {
        return this.$store.state.food.trip;
      },
      set(value) {
        const payload = {
          state: "trip",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    health: {
      get() {
        return this.$store.state.food.health.value;
      },
      set(value) {
        const payload = {
          state: "health",
          field: "value",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    snackbarModel: {
      get() {
        return this.$store.state.food.snackbar.model;
      },
      set(value) {
        const payload = {
          state: "snackbar",
          field: "model",
          value: value,
        };
        this.$store.dispatch("food/updateFoodState", payload);
      },
    },
    healthColor() {
      if (this.health === 0) return "red";
      if (this.health === 1) return "orange";
      if (this.health === 2) return "teal";
      if (this.health === 3) return "green darken-1";
      if (this.health === 4) return "green darken-4";
      return "primary";
    },
  },
  methods: {
    ...mapActions({
      updateSheetsObjAct: "sheet/updateSheetsObj",
    }),
    async insertFoodRecord() {
      if (this.$refs.foodForm.validate()) {
        const range = this.foodSheet;
        let row = [];
        // Get the maxId
        const id = this.sheetsObj[this.foodSheet].maxId + 1;
        // Create a row
        row.push([
          id,
          uuidv4(),
          this.date,
          this.time,
          this.description,
          this.foodType,
          this.trip,
          this.health,
        ]);
        if (this.isAuthenticated) {
          try {
            const gapic = await this.$gapi.getGapiClient(); // init client
            const params = {
              spreadsheetId: this.sheetId,
              valueInputOption: "RAW",
              range: range,
              insertDataOption: "INSERT_ROWS",
            };
            const valueRangeBody = {
              majorDimension: "ROWS",
              values: row,
            };
            await gapic.client.sheets.spreadsheets.values.append(
              params,
              valueRangeBody
            );
            // get snackbar
            const snackbarColor = "secondary";
            const snackbarText = "New meal created!";
            this.getSnackbar(snackbarText, snackbarColor);
            this.clearFoodForm();

            // Refresh fields and sheetsObj
            await this.getEditFormFields(this.sheetId, this.allSheets);
            this.getAllSheetsObj(this.sheetId, this.allSheets);
          } catch (err) {
            console.error("insertFoodTypeError: ", err);
          }
        }
      } else {
        console.log("Food form is not valid");
      }
    },
    clearFoodForm() {
      this.$refs.foodForm.resetValidation();
      this.$store.dispatch("food/updateFoodState", {
        state: "date",
        field: "date",
        value: new Date(new Date().setHours(12, 59, 0, 0))
          .toISOString()
          .substr(0, 10),
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "time",
        field: "time",
        value: null,
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "description",
        value: "",
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "foodType",
        value: "",
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "trip",
        value: "sf",
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "health",
        field: "value",
        value: 2,
      });
    },
    getSnackbar(snackbarText, snackbarColor) {
      this.$store.dispatch("food/updateFoodState", {
        state: "snackbar",
        field: "text",
        value: snackbarText,
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "snackbar",
        field: "color",
        value: snackbarColor,
      });
      this.$store.dispatch("food/updateFoodState", {
        state: "snackbar",
        field: "model",
        value: true,
      });
    },
    async fixHealthScores(sheetId, sheet) {
      // one time to fix health from a scale of 100 to scale of 5
      if (this.isAuthenticated) {
        try {
          const gapic = await this.$gapi.getGapiClient(); // init client
          const range = sheet + "!H2:H";
          const newRange = sheet + "!I2:I";

          const currentHealth = await gapic.client.sheets.spreadsheets.values.batchGet(
            {
              spreadsheetId: sheetId,
              majorDimension: "COLUMNS",
              ranges: range,
            }
          );

          console.log(currentHealth.result.valueRanges[0].values[0]);

          const newHealth = currentHealth.result.valueRanges[0].values[0].map(
            (health) => {
              if (parseFloat(health) <= 20) return 0;
              if (parseFloat(health) <= 40) return 1;
              if (parseFloat(health) <= 60) return 2;
              if (parseFloat(health) <= 80) return 3;
              if (parseFloat(health) <= 100) return 4;
              return 3;
            }
          );

          console.log(newHealth);

          const response = await gapic.client.sheets.spreadsheets.values.batchUpdate(
            {
              spreadsheetId: sheetId,
              valueInputOption: "RAW",
              data: [
                {
                  majorDimension: "COLUMNS",
                  range: newRange,
                  values: [newHealth],
                },
              ],
            }
          );
          console.log(response);
        } catch (err) {
          console.error(err);
        }
      }
    },
  },
  mounted() {
    this.getEditFormFields(this.sheetId, this.allSheets);
    if (!this.sheetsObj[this.foodSheet]) {
      this.getAllSheetsObj(this.sheetId, this.allSheets);
    }
    // this.fixHealthScores(this.sheetId, this.foodSheet); // one time to fix health from a scale of 100 to scale of 5
  },
};
</script>
